import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsEmbedded } from 'hooks/useIsEmbedded';

const CHAT_LAUNCHER_VISIBLE_ROUTES = [
  '/quick_start_guide',
  '/help',
  '/settings',
  '/payroll',
];
const CHAT_LAUNCHER_HIDDEN_ROUTES = [
  '/schedule',
  '/company_timesheets',
  '/timesheets',
];
const CHAT_AUTO_OPEN_ROUTES = ['/help'];

// Utility to match routes
const isRouteMatch = (pathname: string, routes: string[]) =>
  routes.some(route => pathname.startsWith(route));

// Utility to decide chat button visibility
const getSierraChatLauncherVisibility = (
  pathname: string,
  activeConversation: boolean,
  isEmbedded: boolean
) => {
  if (isEmbedded) {
    return false;
  }

  const isVisible = isRouteMatch(pathname, CHAT_LAUNCHER_VISIBLE_ROUTES);
  const isHidden = isRouteMatch(pathname, CHAT_LAUNCHER_HIDDEN_ROUTES);
  return (isVisible || activeConversation) && !isHidden;
};

export const SierraChat = () => {
  const location = useLocation();
  const chatAutoOpened = useRef<boolean>(false);
  const isEmbedded = useIsEmbedded();

  const toggleSierraChatLauncher = useCallback(
    (pathname: string) => {
      const sierraLauncher: HTMLDivElement | null = document.querySelector(
        'div[data-sierra-chat-launcher]'
      );
      if (!sierraLauncher || !pathname) return;

      const { activeConversation = false } = window?.sierraConfig || {};
      const showSierraChatLauncher = getSierraChatLauncherVisibility(
        pathname,
        activeConversation,
        isEmbedded
      );

      const styles = showSierraChatLauncher
        ? { position: 'relative', display: 'block', zIndex: '990' }
        : { position: '', display: 'none', zIndex: '-1' };

      Object.assign(sierraLauncher.style, styles);
    },
    [isEmbedded]
  );

  const toggleSierraChat = useCallback((pathname: string) => {
    if (!pathname) return;

    const sierra = window?.sierra;
    const { activeConversation = false } = window?.sierraConfig || {};
    const isAutoOpenRoute = isRouteMatch(pathname, CHAT_AUTO_OPEN_ROUTES);

    if (isAutoOpenRoute) {
      sierra?.openChatModal?.();
      chatAutoOpened.current = true;
    } else if (chatAutoOpened.current && !activeConversation) {
      sierra?.closeChatModal?.();
      chatAutoOpened.current = false;
    }
  }, []);

  useEffect(() => {
    const onDocumentLoad = () => {
      toggleSierraChatLauncher(location.pathname);
      toggleSierraChat(location.pathname);
    };

    if (document.readyState === 'complete') {
      onDocumentLoad();
    } else {
      window.addEventListener('load', onDocumentLoad);
    }

    return () => {
      window.removeEventListener('load', onDocumentLoad);
    };
  }, [toggleSierraChatLauncher, toggleSierraChat, location.pathname]);

  return null;
};
